import deepmerge from 'deepmerge';
import PluginRegistry from 'src/plugin-system/plugin.registry';
import PluginBaseClass from 'src/plugin-system/plugin.class';
import 'src/plugin-system/plugin.config.manager';

/**
 * this file handles the plugin functionality of shopware
 *
 * to use the PluginManager:
 * ```
  *    window.PluginManager.register(.....);
 *
 *     window.PluginManager.initializePlugins(.....);
 * ```
 *
 * to extend from the base plugin import:
 * ```
 *     import Plugin from 'src/helper/plugin/plugin.class';
 *
 *     export default MyFancyPlugin extends Plugin {}
 * ```
 *
 * methods:
 *
 * // Registers a plugin to the plugin manager.
 * PluginManager.register(pluginName: String, pluginClass: Plugin, selector: String | NodeList | HTMLElement, options?: Object): *;
 *
 * // Removes a plugin from the plugin manager.
 * PluginManager.deregister(pluginName: String): *;
 *
 * // Extends an already existing plugin with a new class or function.
 * // If both names are equal, the plugin will be overridden.
 * PluginManager.extend(fromName: String, newName: String, pluginClass: Plugin, selector: String | NodeList | HTMLElement, options?: Object): boolean;
 *
 * // Returns a list of all registered plugins.
 * PluginManager.getPluginList(): *;
 *
 * // Returns the definition of a plugin.
 * PluginManager.getPlugin(pluginName: String): Map : null;
 *
 * // Returns all registered plugin instances for the passed plugin name.
 * PluginManager.getPluginInstances(pluginName: String): Map : null;
 *
 * // Returns the plugin instance from the passed element selected by plugin mame.
 * PluginManager.getPluginInstanceFromElement(el: HTMLElement, pluginName: String): Object | null;
 *
 * // Returns all plugin instances from the passed element.
 * PluginManager.getPluginInstancesFromElement(el: HTMLElement): Map : null;
 *
 * // Initializes all plugins which are currently registered.
 * PluginManager.initializePlugins(): *;
 *
 * // Initializes a single plugin.
 * PluginManager.initializePlugin(pluginName: String|boolean, selector: String | NodeList | HTMLElement, options?: Object): *;
 *
 * @sw-package framework
 */
/**
 * Upper bound for re-resolving an async plugin that was re-registered while its chunk was loading.
 */
const MAX_ASYNC_RESOLUTION_RETRIES = 3;

class PluginManagerSingleton {

    constructor() {
        this._registry = new PluginRegistry();
    }

    /**
     * Registers a plugin to the plugin manager.
     *
     * @param {string} pluginName
     * @param {Plugin} pluginClass
     * @param {string|NodeList|HTMLElement|HTMLDocument} selector
     * @param {Object} options
     *
     * @returns {*}
     */
    register(pluginName, pluginClass, selector = document, options = {}) {
        if (this._registry.has(pluginName, selector)) {
            console.warn(`Plugin "${pluginName}" is already registered.`);
            return;
        }

        // If we cannot find the prototype of the class, we assume it will be loaded async
        if (!Object.getOwnPropertyDescriptor(pluginClass, 'prototype')) {
            return this._registry.set(pluginName, pluginClass, selector, options, true);
        }

        return this._registry.set(pluginName, pluginClass, selector, options);
    }

    /**
     * Removes a plugin from the plugin manager.
     *
     * @param {string} pluginName
     * @param {string} selector
     *
     * @returns {*}
     */
    deregister(pluginName, selector = document) {
        if (!this._registry.has(pluginName, selector)) {

            if (!this._registry.has(pluginName)) {
                console.warn(`The plugin "${pluginName}" is not registered.`);
                return false;
            }

            return this._registry.delete(pluginName);
        }

        return this._registry.delete(pluginName, selector);
    }

    /**
     * Extends an already existing plugin with a new class or function.
     * If both names are equal, the plugin will be overridden.
     *
     * @param {string} fromName
     * @param {string} newName
     * @param {Plugin} pluginClass
     * @param {string|NodeList|HTMLElement} selector
     * @param {Object} options
     *
     * @returns {boolean}
     */
    extend(fromName, newName, pluginClass, selector = document, options = {}) {
        if (!this._registry.has(fromName, selector)) {
            console.warn(`Trying to extend non-registered plugin "${fromName}". The plugin will not be extended.`);
            return;
        }

        // Register the plugin under a new name
        // If the name is the same, replace it
        if (fromName === newName) {
            const hasInstances = this._registry.get(fromName).get('instances').length > 0;

            this.deregister(fromName, selector);
            const registry = this.register(newName, pluginClass, selector, options);

            // The plugin was already initialized before the override was registered. Re-initialize
            // it so the outdated instances are replaced instead of surviving until the next
            // initializePlugins() call, which may never happen.
            if (hasInstances) {
                this.initializePlugin(newName, selector, options).catch((error) => console.error(error));
            }

            return registry;
        }

        return this._extendPlugin(fromName, newName, pluginClass, selector, options);
    }

    /**
     * Returns a list of all registered plugins.
     *
     * @returns {*}
     */
    getPluginList() {
        return this._registry.keys();
    }

    /**
     * Returns the definition of a plugin.
     *
     * @param {string} pluginName
     * @param {boolean} strict
     *
     * @returns {Map|null}
     */
    getPlugin(pluginName, strict = true) {
        if (!pluginName) {
            console.warn('No plugin name was provided while trying to call getPlugin().');
            return null;
        }

        if (!this._registry.has(pluginName)) {
            if (strict) {
                console.warn(`The plugin "${pluginName}" is not registered. You might need to register it first.`);
                return null;
            } else {
                this._registry.set(pluginName);
            }
        }

        return this._registry.get(pluginName);
    }

    /**
     * Returns all registered plugin instances for the passed plugin name.
     *
     * @param {string} pluginName
     * @returns {Map|null}
     */
    getPluginInstances(pluginName) {
        const plugin = this.getPlugin(pluginName);

        return plugin.get('instances');
    }

    /**
     * Calls a method on all plugin instances.
     *
     * @param {string} pluginName
     * @param {*} methodName
     * @param  {...any} args
     */
    callPluginMethod(pluginName, methodName, ...args) {
        const instances = this.getPluginInstances(pluginName);

        instances.forEach(instance => {
            instance[methodName](...args);
        });
    }

    /**
     * Returns the plugin instance from the passed element selected by plugin Name.
     *
     * @param {HTMLElement} el
     * @param {String} pluginName
     *
     * @returns {Object|null}
     */
    static getPluginInstanceFromElement(el, pluginName) {
        const instances = PluginManagerSingleton.getPluginInstancesFromElement(el);

        return instances.get(pluginName);
    }

    /**
     * Returns all plugin instances from the passed element.
     *
     * @param {HTMLElement} el
     *
     * @returns {Map|null}
     */
    static getPluginInstancesFromElement(el) {
        if (!(el instanceof Node)) {
            console.warn('Passed element in getPluginInstancesFromElement() is not an Html element!');
            return null;
        }

        el.__plugins = el.__plugins || new Map();

        return el.__plugins;
    }

    /**
     * Initializes all plugins which are currently registered.
     *
     * @return {Promise<void>}
     */
    async initializePlugins() {
        const initializationFailures = [];

        await this._fetchAsyncPlugins();

        for (const [pluginName] of Object.entries(this.getPluginList())) {
            if (pluginName) {
                const plugin = this._registry.get(pluginName);
                if (this._isUnresolvedAsyncPlugin(plugin)) {
                    continue;
                }

                if (plugin.has('registrations')) {
                    for (const [, entry] of plugin.get('registrations')) {
                        try {
                            this._initializePlugin(plugin.get('class'), entry.selector, entry.options, plugin.get('name'));
                        } catch (failure) {
                            initializationFailures.push(failure);
                        }
                    }
                }
            }
        }

        initializationFailures.forEach((failure) => {
            console.error(failure);
        });

        return Promise.resolve();
    }

    /**
     * Initializes all registered plugins, but only for elements within the parent element.
     *
     * @param {HTMLElement} parentElement
     * @returns {Promise<void>}
     */
    async initializePluginsInParentElement(parentElement) {
        const initializationFailures = [];

        await this._fetchAsyncPlugins();

        for (const [pluginName] of Object.entries(this.getPluginList())) {
            if (pluginName) {
                const plugin = this._registry.get(pluginName);
                if (this._isUnresolvedAsyncPlugin(plugin)) {
                    continue;
                }

                if (plugin.has('registrations')) {
                    for (const [, entry] of plugin.get('registrations')) {
                        try {
                            this._initializePlugin(plugin.get('class'), entry.selector, entry.options, plugin.get('name'), parentElement);
                        } catch (failure) {
                            initializationFailures.push(failure);
                        }
                    }
                }
            }
        }

        initializationFailures.forEach((failure) => {
            console.error(failure);
        });

        return Promise.resolve();
    }

    /**
     * Fetches all async plugins and sets their class to the PluginRegistry.
     *
     * @return {Promise<void>}
     * @private
     */
    async _fetchAsyncPlugins(depth = 0) {
        // Collect all async plugins that need to be fetched via async import and Promise.all()
        // [
        //   { pluginName: 'Example', pluginClassPromise: () => import('./example.plugin') },
        //   { pluginName: 'FooBar', pluginClassPromise: () => import('./foo-bar.plugin') },
        // ]
        const queue = [];

        // Will contain the fetched plugin classes
        // [
        //   Module: { __esModule: true, default: class Example ... },
        //   Module: { __esModule: true, default: class FooBar ... },
        // ]
        let fetchedPluginClasses = [];

        for (const [pluginName] of Object.entries(this.getPluginList())) {
            if (!pluginName) {
                continue;
            }

            if (!this._registry.has(pluginName)) {
                console.warn(`The plugin "${pluginName}" is not registered.`);
                continue;
            }

            const plugin = this._registry.get(pluginName);

            if (!plugin.has('registrations')) {
                continue;
            }

            for (const [, entry] of plugin.get('registrations')) {
                if (!plugin.get('async')) {
                    continue;
                }

                let selector = entry.selector;

                if (selector instanceof Node) {
                    queue.push({ pluginName: pluginName, pluginClassPromise: plugin.get('class') });
                    continue;
                }

                if (typeof selector === 'string') {
                    selector = PluginManagerSingleton._queryElements(selector);
                }

                if (selector.length > 0) {
                    queue.push({ pluginName: pluginName, pluginClassPromise: plugin.get('class') });
                }
            }
        }

        if (!queue.length) {
            return;
        }

        // Fetch all needed plugins while keeping a single failing plugin from stopping the others.
        fetchedPluginClasses = await Promise.all(queue.map((queueItem) => {
            return this._loadAsyncPluginClass(queueItem.pluginName, queueItem.pluginClassPromise);
        }));

        const staleResolutions = new Set();

        // Set the fetched plugin classes to the registry, so they can be initialized later.
        queue.forEach((plugin, index) => {
            const pluginClass = fetchedPluginClasses[index];
            if (!pluginClass) {
                return;
            }

            const pluginName = plugin.pluginName;
            const pluginFromRegistry = this._registry.get(pluginName);

            if (!this._setResolvedPluginClass(pluginFromRegistry, pluginClass, plugin.pluginClassPromise)) {
                staleResolutions.add(pluginName);
            }
        });

        if (!staleResolutions.size) {
            return;
        }

        // A plugin that was re-registered while we were loading is still unresolved, fetch it again.
        if (depth < MAX_ASYNC_RESOLUTION_RETRIES) {
            await this._fetchAsyncPlugins(depth + 1);

            return;
        }

        console.warn(`The plugin(s) "${Array.from(staleResolutions).join('", "')}" were re-registered while loading more often than the plugin manager retries. They will not be initialized.`);
    }

    /**
     * @param {Object} pluginFromRegistry
     * @param {String|NodeList|HTMLElement} selector
     * @return {Promise<void>}
     * @private
     */
    async _fetchAsyncPlugin(pluginFromRegistry, selector, depth = 0) {
        if (!pluginFromRegistry.get('async')) {
            return;
        }

        const pluginName = pluginFromRegistry.get('name');
        const originalSelector = selector;

        let needsFetch = false;
        if (selector instanceof Node) {
            needsFetch = true;
        }

        if (typeof selector === 'string') {
            selector = PluginManagerSingleton._queryElements(selector);
            needsFetch = !!selector.length;
        }

        if (!needsFetch) {
            return;
        }

        const importFn = pluginFromRegistry.get('class');
        const pluginClass = await this._loadAsyncPluginClass(pluginName, importFn);
        if (!pluginClass) {
            return;
        }

        if (this._setResolvedPluginClass(pluginFromRegistry, pluginClass, importFn)) {
            return;
        }

        if (depth < MAX_ASYNC_RESOLUTION_RETRIES) {
            await this._fetchAsyncPlugin(pluginFromRegistry, originalSelector, depth + 1);

            return;
        }

        console.warn(`The plugin "${pluginName}" was re-registered while loading more often than the plugin manager retries. It will not be initialized.`);
    }

    /**
     * @param {string} pluginName
     * @param {Function} importFn - lazy import registered via PluginManager.register()
     * @returns {Promise<typeof Plugin|null>}
     * @private
     */
    async _loadAsyncPluginClass(pluginName, importFn) {
        try {
            const module = await importFn();

            if (!module?.default) {
                console.warn(`The async plugin "${pluginName}" could not be loaded and will be skipped.`);
                return null;
            }

            return module.default;
        } catch (error) {
            console.warn(`The async plugin "${pluginName}" could not be loaded and will be skipped.`, error);
            return null;
        }
    }

    /**
     * @param {Object} pluginFromRegistry
     * @param {typeof Plugin} pluginClass
     * @param {Function} importFn - the lazy import that produced the class
     * @returns {boolean} false when the registration changed while the class was loading
     * @private
     */
    _setResolvedPluginClass(pluginFromRegistry, pluginClass, importFn) {
        const currentClass = pluginFromRegistry.get('class');

        // Already resolved to the very same class by a concurrent pass or by a second registration
        // of the same plugin. Idempotent, not stale.
        if (currentClass === pluginClass) {
            pluginFromRegistry.set('async', false);

            return true;
        }

        // The plugin can be re-registered, for example overridden by an app or theme, while its
        // chunk is still loading. The outdated resolution must never win over that registration.
        if (currentClass !== importFn) {
            return false;
        }

        pluginFromRegistry.set('async', false);
        pluginFromRegistry.set('class', pluginClass);

        return true;
    }

    /**
     * @param {Object} plugin
     * @returns {boolean}
     * @private
     */
    _isUnresolvedAsyncPlugin(plugin) {
        return plugin.get('async');
    }

    /**
     * Initializes a single plugin.
     *
     * @param {string} pluginName
     * @param {String|NodeList|HTMLElement} selector
     * @param {Object} options
     */
    async initializePlugin(pluginName, selector, options) {
        let plugin;
        let pluginClass;
        let mergedOptions;

        if (this._registry.has(pluginName, selector)) {
            plugin = this._registry.get(pluginName, selector);
            await this._fetchAsyncPlugin(plugin, selector);

            if (this._isUnresolvedAsyncPlugin(plugin)) {
                return Promise.resolve();
            }

            const registrationOptions = plugin.get('registrations').get(selector);
            pluginClass = plugin.get('class');
            mergedOptions = deepmerge(pluginClass.options || {}, deepmerge(registrationOptions.options || {}, options || {}));
        } else {
            plugin = this._registry.get(pluginName);
            await this._fetchAsyncPlugin(plugin, selector);

            if (this._isUnresolvedAsyncPlugin(plugin)) {
                return Promise.resolve();
            }

            pluginClass = plugin.get('class');
            mergedOptions = deepmerge(pluginClass.options || {}, options || {});
        }

        try {
            this._initializePlugin(pluginClass, selector, mergedOptions, plugin.get('name'));
        } catch (failure) {
            console.error(failure);
        }

        return Promise.resolve();
    }

    /**
     * Executes a vanilla plugin class.
     *
     * @param {Plugin} pluginClass
     * @param {String|NodeList|HTMLElement} selector
     * @param {Object} options
     * @param {string} pluginName
     * @param {HTMLElement} context - Optional parent element to scope the selector query to.
     */
    _initializePlugin(pluginClass, selector, options, pluginName = false, context = null) {
        if (selector instanceof Node) {
            if (context && !context.contains(selector)) {
                return;
            }
            return PluginManagerSingleton._initializePluginOnElement(selector, pluginClass, options, pluginName);
        }

        if (typeof selector === 'string') {
            selector = PluginManagerSingleton._queryElements(selector, context);
        }

        return Array.from(selector).forEach(el => {
            if (context && !context.contains(el)) {
                return;
            }
            PluginManagerSingleton._initializePluginOnElement(el, pluginClass, options, pluginName);
        });
    }

    /**
     * Determines the way to query the elements.
     *
     * [data-*] => querySelectorAll
     * #fooBar => getElementById
     * #foo_bar => getElementById
     * #foo-bar => getElementById
     * #foo .bar => querySelectorAll
     * .fooBar => getElementsByClassName
     * .FOO => getElementsByClassName
     * .foo_bar => getElementsByClassName
     * .foo-bar => getElementsByClassName
     * .foo .bar => querySelectorAll
     *
     * FOO => getElementsByTagName
     * FOO .bar => querySelectorAll
     *
     * For performance reason used regex based on common characters `a-zA-Z1-9_-`
     * instead of the entire compatible characters
     *
     * @param {string} selector
     * @param {HTMLElement} context - Optional parent element to scope the query to.
     *
     * @return {NodeList|HTMLCollection|Array}
     */
    static _queryElements(selector, context = null) {
        if (context) {
            return context.querySelectorAll(selector);
        }

        if (selector.startsWith('.')) {
            const regexEl = /^\.([\w-]+)$/.exec(selector);
            if (regexEl) {
                return document.getElementsByClassName(regexEl[1]);
            }
        } else if (selector.startsWith('#')) {
            const regexEl = /^#([\w-]+)$/.exec(selector);
            if (regexEl) {
                const el = document.getElementById(regexEl[1]);

                return (el) ? [el] : [];
            }
        } else if (/^([\w-]+)$/.exec(selector)) {
            return document.getElementsByTagName(selector);
        }

        return document.querySelectorAll(selector);
    }

    /**
     * Executes a vanilla plugin class on the passed element.
     *
     * @param {Node|HTMLElement} el
     * @param {Plugin} pluginClass
     * @param {Object} options
     * @param {string} pluginName
     * @private
     */
    static _initializePluginOnElement(el, pluginClass, options, pluginName) {
        if (typeof pluginClass !== 'function') {
            console.warn('The passed plugin is not a function or a class.');
            return null;
        }

        const instance = PluginManager.getPluginInstanceFromElement(el, pluginName);
        if (!instance) {
            return new pluginClass(el, options, pluginName);
        }

        // The registered class changed after this instance was created, for example because an app
        // or theme overrode the plugin. Rebuild the element with the current class, otherwise the
        // override would never run on elements that were already initialized.
        // This is only sound because every initialization pass carries the class that is currently
        // in the registry, see _setResolvedPluginClass().
        if (!(instance instanceof pluginClass)) {
            PluginManagerSingleton._destroyPluginInstance(el, instance, pluginName);

            return new pluginClass(el, options, pluginName);
        }

        return instance._update();
    }

    /**
     * Tears an outdated plugin instance down and removes it from all instance registries.
     *
     * @param {Node|HTMLElement} el
     * @param {Plugin} instance
     * @param {string} pluginName
     * @private
     */
    static _destroyPluginInstance(el, instance, pluginName) {
        if (instance.destroy === PluginBaseClass.prototype.destroy) {
            console.warn(`The plugin "${pluginName}" does not implement destroy(). Everything the replaced instance registered outside of itself, for example event listeners added with addEventListener, stays active.`);
        }

        // Kept in separate try blocks on purpose: a throwing destroy() must not skip the emitter
        // reset, otherwise the listeners this method exists to remove would stay attached.
        try {
            if (typeof instance.destroy === 'function') {
                instance.destroy();
            }
        } catch (error) {
            console.warn(`The outdated instance of plugin "${pluginName}" could not be destroyed.`, error);
        }

        try {
            if (instance.$emitter) {
                instance.$emitter.reset();
            }
        } catch (error) {
            console.warn(`The event emitter of the outdated instance of plugin "${pluginName}" could not be reset.`, error);
        }

        PluginManager.getPluginInstancesFromElement(el).delete(pluginName);

        const instances = PluginManagerInstance._registry.has(pluginName)
            ? PluginManagerInstance._registry.get(pluginName).get('instances')
            : null;

        if (Array.isArray(instances)) {
            const index = instances.indexOf(instance);

            if (index > -1) {
                instances.splice(index, 1);
            }
        }
    }

    /**
     * extends a plugin class with another class or function.
     *
     * @param {string} fromName
     * @param {string} newName
     * @param {Plugin} pluginClass
     * @param {string|NodeList|HTMLElement} selector
     * @param {Object} options
     *
     * @returns {*}
     * @private
     */
    _extendPlugin(fromName, newName, pluginClass, selector, options = {}) {
        if (!this._registry.has(fromName, selector)) {
            console.warn(`Trying to extend non-registered plugin "${fromName}". The plugin will not be extended.`);
            return;
        }

        // get current plugin
        const extendFrom = this._registry.get(fromName);
        const parentPlugin = extendFrom.get('class');

        // The parent is an async plugin that is not loaded yet, so it cannot be extended right now.
        // Register a lazy import that builds the extended class once the parent is available.
        // Static parent options are merged by the plugin base class at construction time.
        if (extendFrom.get('async')) {
            // The promise is memoized, not just its result: concurrent initialization passes can
            // call this before the first call resolved, and a new class per call would break the
            // identity check in _setResolvedPluginClass().
            let extendedPluginPromise = null;

            return this.register(newName, () => {
                if (extendedPluginPromise) {
                    return extendedPluginPromise;
                }

                extendedPluginPromise = (async () => {
                    const currentParent = extendFrom.get('class');

                    // The parent may have been resolved in the meantime by an unrelated init pass.
                    const resolvedParent = Object.getOwnPropertyDescriptor(currentParent, 'prototype')
                        ? currentParent
                        : await this._loadAsyncPluginClass(fromName, currentParent);

                    if (!resolvedParent) {
                        // Drop the memo so a later pass can retry once the parent is available.
                        extendedPluginPromise = null;

                        return { default: null };
                    }

                    return { default: PluginManagerSingleton._buildExtendedPlugin(resolvedParent, pluginClass) };
                })();

                return extendedPluginPromise;
            }, selector, options);
        }

        const mergedOptions = deepmerge(parentPlugin.options || {}, options || {});

        return this.register(newName, PluginManagerSingleton._buildExtendedPlugin(parentPlugin, pluginClass), selector, mergedOptions);
    }

    /**
     * Creates a new plugin class that extends the passed parent plugin.
     *
     * @param {typeof Plugin} parentPlugin
     * @param {Object} pluginClass
     *
     * @returns {typeof Plugin}
     * @private
     */
    static _buildExtendedPlugin(parentPlugin, pluginClass) {
        class InternallyExtendedPlugin extends parentPlugin {
        }

        // Extend the plugin with the new definitions. The prototype object is mutated in place:
        // the `prototype` property of a class is non-writable, so assigning to it throws in strict
        // mode, which every ES module is.
        Object.assign(InternallyExtendedPlugin.prototype, pluginClass);
        InternallyExtendedPlugin.prototype.constructor = InternallyExtendedPlugin;

        return InternallyExtendedPlugin;
    }

}

/**
 * Create the PluginManager instance.
 * @type {Readonly<PluginManagerSingleton>}
 */
export const PluginManagerInstance = Object.freeze(new PluginManagerSingleton());

export default class PluginManager {

    constructor() {
        window.PluginManager = this;
    }

    /**
     * Registers a plugin to the plugin manager.
     *
     * @param {string} pluginName
     * @param {function(): Promise<{readonly default?: *}>} pluginClass
     * @param {string|NodeList|HTMLElement} selector
     * @param {Object} options
     *
     * @returns {*}
     */
    static register(pluginName, pluginClass, selector = document, options = {}) {
        return PluginManagerInstance.register(pluginName, pluginClass, selector, options);
    }

    /**
     * Removes a plugin from the plugin manager.
     *
     * @param {string} pluginName
     * @param {string} selector
     *
     * @returns {*}
     */
    static deregister(pluginName, selector) {
        return PluginManagerInstance.deregister(pluginName, selector);
    }

    /**
     * Extends an already existing plugin with a new class or function.
     * If both names are equal, the plugin will be overridden.
     *
     * @param {string} fromName
     * @param {string} newName
     * @param {Plugin} pluginClass
     * @param {string|NodeList|HTMLElement} selector
     * @param {Object} options
     *
     * @returns {boolean}
     */
    static extend(fromName, newName, pluginClass, selector, options = {}) {
        return PluginManagerInstance.extend(fromName, newName, pluginClass, selector, options);
    }

    static override(overrideName, pluginClass, selector, options = {}) {
        return PluginManagerInstance.extend(overrideName, overrideName, pluginClass, selector, options);
    }

    /**
     * Returns a list of all registered plugins.
     *
     * @returns {*}
     */
    static getPluginList() {
        return PluginManagerInstance.getPluginList();
    }

    /**
     * Returns the definition of a plugin.
     *
     * @returns {*}
     */
    static getPlugin(pluginName) {
        return PluginManagerInstance.getPlugin(pluginName);
    }

    /**
     * Returns all registered plugin instances for the passed plugin name..
     *
     * @param {string} pluginName
     *
     * @returns {Map|null}
     */
    static getPluginInstances(pluginName) {
        return PluginManagerInstance.getPluginInstances(pluginName);
    }

    /**
     * Returns the plugin instance from the passed element selected by plugin Name.
     *
     * @param {HTMLElement} el
     * @param {String} pluginName
     *
     * @returns {Object|null}
     */
    static getPluginInstanceFromElement(el, pluginName) {
        return PluginManagerSingleton.getPluginInstanceFromElement(el, pluginName);
    }

    /**
     * Returns all plugin instances from the passed element.
     *
     * @param {HTMLElement} el
     *
     * @returns {Map|null}
     */
    static getPluginInstancesFromElement(el) {
        return PluginManagerSingleton.getPluginInstancesFromElement(el);
    }

    /**
     * Initializes all plugins which are currently registered.
     *
     * @return {Promise<void>}
     */
    static initializePlugins() {
        return PluginManagerInstance.initializePlugins();
    }

    /**
     * Initializes all registered plugins, but only for elements within the parent element.
     *
     * @param {HTMLElement} parentElement
     * @return {Promise<void>}
     */
    static initializePluginsInParentElement(parentElement) {
        return PluginManagerInstance.initializePluginsInParentElement(parentElement);
    }

    /**
     * Initializes a single plugin.
     *
     * @param {string} pluginName
     * @param {String|NodeList|HTMLElement} selector
     * @param {Object} options
     * @returns {Promise<void>}
     */
    static initializePlugin(pluginName, selector, options) {
        return PluginManagerInstance.initializePlugin(pluginName, selector, options);
    }

    /**
     * Calls a method on all plugin instances.
     *
     * @param {string} pluginName
     * @param {string} methodName
     * @param  {...any} args
     * @returns
     */
    static callPluginMethod(pluginName, methodName, ...args) {
        return PluginManagerInstance.callPluginMethod(pluginName, methodName, ...args);
    }
}

window.PluginManager = PluginManager;
window.PluginBaseClass = PluginBaseClass;
